import React, { useState, useRef, useEffect } from "react";

import {
  EnvelopeIcon,
  XMarkIcon,
  PaperClipIcon,
  DocumentIcon,
  PhotoIcon,
} from "@heroicons/react/24/solid";

import classes from "./chatPanel.module.css";

export const ChatPanel = () => {
  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: "other",
      text: "Hello, please review the lot details.",
      time: "10:30 AM",
    },
    {
      id: 2,
      sender: "me",
      text: "Sure, I am checking it now.",
      time: "10:32 AM",
    },
  ]);

  const [input, setInput] = useState("");

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);

  const unreadCount = 2;

  /* ---------- Auto Scroll ---------- */

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
      inputRef.current?.focus();
    }
  }, [messages, isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
    });
  };

  /* ---------- Time ---------- */

  const getCurrentTime = () => {
    return new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /* ---------- Send Message ---------- */

  const handleSend = () => {
    if (!input.trim()) return;

    const newMessage = {
      id: Date.now(),
      sender: "me",
      text: input,
      time: getCurrentTime(),
    };

    setMessages((prev) => [...prev, newMessage]);
    setInput("");

    simulateReply();
  };

  /* ---------- File Upload ---------- */

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const type = file.type;

    let category = "file";

    if (type.startsWith("image/")) category = "image";
    else if (type.includes("pdf")) category = "pdf";
    else if (type.includes("sheet") || type.includes("excel"))
      category = "excel";

    const newMessage = {
      id: Date.now(),
      sender: "me",
      file: {
        name: file.name,
        size: (file.size / 1024).toFixed(1) + " KB",
        category,
        url: URL.createObjectURL(file),
      },
      time: getCurrentTime(),
    };

    setMessages((prev) => [...prev, newMessage]);

    e.target.value = null;

    simulateReply();
  };

  /* ---------- Fake Reply ---------- */

  const simulateReply = () => {
    setTimeout(() => {
      const reply = {
        id: Date.now() + 1,
        sender: "other",
        text: "Received 👍",
        time: getCurrentTime(),
      };

      setMessages((prev) => [...prev, reply]);
    }, 1500);
  };

  /* ---------- Enter Send ---------- */

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSend();
  };

  return (
    <>
      {/* Action Button */}
      <div className={classes.actionContainer}>
        {/* <h3 className={classes.actionText}>
          <span className={classes.alertDot} />
          Action Required
        </h3> */}

        <button
          className={classes.chatButton}
          onClick={() => setIsOpen(true)}
        >
          <EnvelopeIcon width={18} />

          Chat

          {unreadCount > 0 && (
            <span className={classes.unreadBadge}>
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Overlay */}
      {isOpen && (
        <div
          className={classes.overlay}
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Panel */}
      <div
        className={`${classes.panel} ${
          isOpen ? classes.open : ""
        }`}
      >
        {/* Header */}
        <div className={classes.header}>
          <div>
            <div className={classes.userName}>
              Rahul Sharma
            </div>

            <div className={classes.userStatus}>
              ● Online
            </div>
          </div>

          <XMarkIcon
            width={22}
            className={classes.closeIcon}
            onClick={() => setIsOpen(false)}
          />
        </div>

        {/* Messages */}
        <div className={classes.messages}>
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`${classes.messageRow}
              ${
                msg.sender === "me"
                  ? classes.myRow
                  : classes.otherRow
              }`}
            >
              <div
                className={`${classes.messageBubble}
                ${
                  msg.sender === "me"
                    ? classes.myBubble
                    : classes.otherBubble
                }`}
              >
                {/* TEXT */}
                {msg.text && msg.text}

                {/* FILE */}
                {msg.file && (
                  <div className={classes.attachment}>
                    {msg.file.category === "image" ? (
                      <img
                        src={msg.file.url}
                        className={classes.imagePreview}
                        alt="file"
                      />
                    ) : (
                      <div className={classes.fileRow}>
                        <DocumentIcon width={18} />

                        <div>
                          <div
                            className={classes.fileName}
                          >
                            {msg.file.name}
                          </div>

                          <div
                            className={classes.fileSize}
                          >
                            {msg.file.size}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className={classes.messageTime}>
                  {msg.time}
                </div>
              </div>
            </div>
          ))}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className={classes.inputContainer}>
          <button
            className={classes.attachButton}
            onClick={() =>
              fileInputRef.current.click()
            }
          >
            <PaperClipIcon width={18} />
          </button>

          <input
            type="file"
            hidden
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept="image/*,.pdf,.xls,.xlsx"
          />

          <input
            ref={inputRef}
            className={classes.input}
            value={input}
            onChange={(e) =>
              setInput(e.target.value)
            }
            onKeyDown={handleKeyDown}
            placeholder="Write a message..."
          />

          <button
            className={classes.sendButton}
            onClick={handleSend}
          >
            Send
          </button>
        </div>
      </div>
    </>
  );
};